import React from 'react';
import { useLocation } from 'react-router-dom';
import { Fish, Plus } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/shared/components/ui/Button';
import { buildRoute } from '@/shared/lib/constants';
import { TabNavigation } from '@/features/shared/components/TabNavigation';
import type { Tab } from '@/features/shared/components/TabNavigation';
import { PondFilter } from '@/features/shared/components/PondFilter';
import type { PondOption } from '@/features/shared/components/PondFilter';
import { useFarmFromUrl } from '@/shared/hooks/useUrlSync';
import { useFeaturePageState } from '@/shared/hooks/useFeaturePageState';
import { PageLoading } from '@/shared/components/ui/LoadingSpinner';
import { FarmRequiredGuard } from '@/shared/components/FarmRequiredGuard';
import { FishHealthRecordsTab } from './FishHealthRecordsTab';
import { FishHealthTrendsTab } from './FishHealthTrendsTab';
import { FishHealthExportButton } from './FishHealthExportButton';

type TabId = 'records' | 'trends';

const tabs: Tab[] = [
  { id: 'records', label: 'Health Records' },
  { id: 'trends', label: 'Trends & Analytics' },
];

// Tab configuration for fish health feature (path-based routing)
const FISH_HEALTH_TABS = [
  { id: 'records', path: '' },
  { id: 'trends', path: '/trends' },
];

export const FishHealthPage: React.FC = () => {
  const { farmId, farm, isLoading: isFarmLoading } = useFarmFromUrl();
  const location = useLocation();
  const navigate = useNavigate();

  // Determine active tab from URL path (path-based routing)
  const activeTab: TabId = location.pathname.endsWith('/trends') ? 'trends' : 'records';

  // Use shared hook for pond selection and tab navigation
  // This handles: pond fetching, URL sync, localStorage restoration, and tab navigation
  const {
    selectedPondId,
    setSelectedPondId,
    navigateToTab,
    ponds,
    isPondsLoading,
  } = useFeaturePageState({
    farmId,
    feature: 'fish-health',
    tabs: FISH_HEALTH_TABS,
    currentTab: activeTab,
  });

  // Show loading state
  if (isFarmLoading || (isPondsLoading && ponds.length === 0)) {
    return <PageLoading message="Loading..." />;
  }

  // Show farm selection guard if no farmId
  if (!farmId) {
    return (
      <FarmRequiredGuard farmId={farmId} isLoading={isFarmLoading}>
        {null}
      </FarmRequiredGuard>
    );
  }

  const pondOptions: PondOption[] = ponds.map((pond) => ({
    id: pond.id,
    name: pond.name,
  }));

  return (
    <div className="space-y-6">
      {/* Ponds Loading Banner */}
      {isPondsLoading && (
        <div className="rounded-lg bg-blue-50 border border-blue-200/50 p-4">
          <p className="font-roboto text-sm text-blue-900">Loading ponds...</p>
        </div>
      )}

      {/* Page Header */}
      <div>
        <div className="flex items-center gap-2 text-sm text-gray-600 mb-2">
          <span>{farm?.name}</span>
          <span>›</span>
          <span className="text-gray-900 font-medium">Fish Health</span>
        </div>
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="p-3 bg-blue-50 rounded-lg border border-blue-200/50">
              <Fish className="h-6 w-6 text-blue-600" />
            </div>
            <div>
              <h1 className="font-roboto text-2xl font-bold text-gray-900">Fish Health Monitoring</h1>
              <p className="font-roboto mt-1 text-sm text-gray-600">
                {selectedPondId && activeTab === 'records'
                  ? `Health records for ${ponds.find((p) => p.id === selectedPondId)?.name ?? 'selected pond'}`
                  : activeTab === 'trends'
                  ? 'Track fish health trends across ponds'
                  : 'Monitor mortality, parasites, and fish condition'}
              </p>
            </div>
          </div>
          {activeTab === 'records' && (
            <div className="flex items-center gap-2">
              <Button
                variant="primary"
                onClick={() => navigate(buildRoute.fishHealthNew(farmId, selectedPondId))}
                disabled={!selectedPondId}
                title={!selectedPondId ? "Select a pond first" : undefined}
              >
                <Plus className="h-4 w-4 mr-1" />
                Add Check
              </Button>
              <FishHealthExportButton
                farmId={farmId}
                pondId={selectedPondId}
                pondName={ponds.find((p) => p.id === selectedPondId)?.name}
                disabled={!selectedPondId}
              />
            </div>
          )}
        </div>
      </div>

      {/* Tab Navigation */}
      <TabNavigation tabs={tabs} activeTab={activeTab} onChange={navigateToTab} />

      {/* Pond Filter (only show on Records tab) */}
      {activeTab === 'records' && ponds.length > 0 && (
        <div className="max-w-xs">
          <PondFilter
            ponds={pondOptions}
            selectedPondId={selectedPondId}
            onChange={setSelectedPondId}
            label="Filter by Pond"
          />
        </div>
      )}

      {/* Tab Content */}
      {activeTab === 'records' ? (
        <FishHealthRecordsTab farmId={farmId} selectedPondId={selectedPondId} />
      ) : (
        <FishHealthTrendsTab farmId={farmId} ponds={ponds} />
      )}
    </div>
  );
};
